import { useCallback, useEffect, useRef, useState } from 'react'

import {
    SIDEBAR_STATE,
    SIDEBAR_HOVER_OPEN_DELAY,
    SIDEBAR_HOVER_CLOSE_DELAY,
    SIDEBAR_TRANSITION_FALLBACK,
    SIDEBAR_COLLAPSED_KEY,
    SIDEBAR_PIN_AVAILABLE_QUERY,
    SIDEBAR_PIN_BY_DEFAULT_QUERY,
} from '../constants/sidebar'

// localStorage throws in Safari private mode, so every access is guarded.
// Returns null when the teacher has never chosen (or storage is unavailable).
export const readStoredCollapsed = () => {
    try {
        const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
        return stored === null ? null : stored === 'true'
    } catch (error) {
        return null
    }
}

export const writeStoredCollapsed = (value) => {
    try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value))
    } catch (error) {
        console.error('Failed to persist sidebar state:', error)
    }
}

const matchesQuery = (query) => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : true)
const pinIsAvailable = () => matchesQuery(SIDEBAR_PIN_AVAILABLE_QUERY)

// A stored choice wins; otherwise pin only on large screens. Not persisted on purpose,
// see SIDEBAR_PIN_BY_DEFAULT_QUERY.
const initialPinnedPref = () => {
    const storedCollapsed = readStoredCollapsed()
    return storedCollapsed === null ? matchesQuery(SIDEBAR_PIN_BY_DEFAULT_QUERY) : !storedCollapsed
}

// Focus that arrived by keyboard. A mouse click also focuses the link or pin it lands
// on, and that focus must not hold the panel open after the pointer leaves, so plain
// `contains(document.activeElement)` is not enough. Browsers without :focus-visible
// (Safari < 15.4) throw on the selector; `fallback` picks the safer answer per caller.
const hasKeyboardFocusWithin = (root, fallback) => {
    const active = document.activeElement
    if (!root || !active || !root.contains(active)) return false
    try {
        return active.matches(':focus-visible')
    } catch (error) {
        return fallback
    }
}

/**
 * Single source of truth for the sidebar's collapsed | peek | pinned state.
 *
 * - collapsed → peek: pointer rests on the rail for SIDEBAR_HOVER_OPEN_DELAY, keyboard
 *   focus enters it, or a touch lands on it (touch has no hover).
 * - peek → collapsed: pointer gone for SIDEBAR_HOVER_CLOSE_DELAY with no keyboard focus
 *   inside, a press outside the panel, or Escape.
 * - initial pin: the stored choice, else pinned only at SIDEBAR_PIN_BY_DEFAULT_QUERY.
 * - pin/unpin never moves the panel: unpinning leaves it open as a peek that closes by
 *   the normal rule once the pointer leaves.
 *
 * @param {object} options
 * @param {boolean} options.forceUnpinned route-level override (child-facing screens);
 *   hides the pin and ignores the stored preference without overwriting it.
 * @returns {{
 *   rootRef: import('react').RefObject<HTMLElement>,
 *   state: 'collapsed' | 'peek' | 'pinned',
 *   pinnedPref: boolean,
 *   showPin: boolean,
 *   togglePin: () => void,
 *   consumeUserToggle: () => boolean,
 *   handlers: object,
 * }}
 */
const useSidebarState = ({ forceUnpinned = false } = {}) => {
    const rootRef = useRef(null)
    const [pinnedPref, setPinnedPref] = useState(initialPinnedPref)
    const [pinAvailable, setPinAvailable] = useState(pinIsAvailable)
    const [open, setOpen] = useState(false)

    const showPin = pinAvailable && !forceUnpinned
    const pinned = pinnedPref && showPin
    const state = pinned ? SIDEBAR_STATE.PINNED : open ? SIDEBAR_STATE.PEEK : SIDEBAR_STATE.COLLAPSED

    // Handlers read the latest state through refs so they can stay referentially stable.
    const stateRef = useRef(state)
    stateRef.current = state
    const openTimer = useRef(null)
    const closeTimer = useRef(null)
    const hovering = useRef(false)
    // Escape closes the peek while focus stays put; without this the very next Tab
    // (a focusin) would reopen it.
    const escapeDismissed = useRef(false)
    // When the panel last started collapsing, so a pointer that comes straight back
    // reverses the sweep immediately instead of waiting out the hover-intent delay.
    const collapsedAt = useRef(0)
    // Set by togglePin so the page-shift FLIP only runs for a deliberate pin/unpin,
    // not for a breakpoint crossing or a route that forces the rail closed.
    const userToggled = useRef(false)

    const clearTimers = useCallback(() => {
        clearTimeout(openTimer.current)
        clearTimeout(closeTimer.current)
    }, [])

    useEffect(() => clearTimers, [clearTimers])

    useEffect(() => {
        if (state === SIDEBAR_STATE.COLLAPSED) collapsedAt.current = Date.now()
    }, [state])

    const collapse = useCallback(() => {
        clearTimers()
        setOpen(false)
    }, [clearTimers])

    const scheduleClose = useCallback(() => {
        clearTimers()
        closeTimer.current = setTimeout(() => {
            if (hovering.current || hasKeyboardFocusWithin(rootRef.current, false)) return
            setOpen(false)
        }, SIDEBAR_HOVER_CLOSE_DELAY)
    }, [clearTimers])

    // Track whether the pin control is on screen; below the breakpoint the preference is ignored.
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined

        const mediaQuery = window.matchMedia(SIDEBAR_PIN_AVAILABLE_QUERY)
        const handleChange = (event) => setPinAvailable(event.matches)

        // Safari < 14 only implements the deprecated addListener/removeListener pair.
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange)
        } else {
            mediaQuery.addListener(handleChange)
        }

        // The breakpoint may have been crossed between the initial render and this effect.
        setPinAvailable(mediaQuery.matches)

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange)
            } else {
                mediaQuery.removeListener(handleChange)
            }
        }
    }, [])

    // A press anywhere outside an open peek closes it. This is the only way out for
    // touch, and for a mouse it just skips the close delay.
    useEffect(() => {
        if (state !== SIDEBAR_STATE.PEEK) return undefined

        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) collapse()
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        return () => document.removeEventListener('pointerdown', handlePointerDown, true)
    }, [state, collapse])

    // pointerenter/leave do not fire for moves between children, which is what
    // mouseover/mouseout got wrong. Touch is ignored here: a tap emulates enter/leave
    // and would open-then-close; onPointerDown handles it instead.
    const onPointerEnter = useCallback((event) => {
        if (event.pointerType === 'touch') return
        hovering.current = true
        clearTimeout(closeTimer.current)
        if (stateRef.current !== SIDEBAR_STATE.COLLAPSED) return

        clearTimeout(openTimer.current)
        const recentlyCollapsed = Date.now() - collapsedAt.current < SIDEBAR_TRANSITION_FALLBACK
        if (recentlyCollapsed) {
            setOpen(true)
            return
        }
        openTimer.current = setTimeout(() => setOpen(true), SIDEBAR_HOVER_OPEN_DELAY)
    }, [])

    const onPointerLeave = useCallback(
        (event) => {
            if (event.pointerType === 'touch') return
            hovering.current = false
            clearTimeout(openTimer.current)
            if (stateRef.current === SIDEBAR_STATE.PEEK) {
                scheduleClose()
            } else if (stateRef.current === SIDEBAR_STATE.PINNED) {
                // Invisible while pinned, but it is what a breakpoint crossing falls back
                // to, and the pointer is no longer there to justify a peek.
                setOpen(false)
            }
        },
        [scheduleClose]
    )

    const onPointerDown = useCallback(
        (event) => {
            if (event.pointerType !== 'touch' || stateRef.current !== SIDEBAR_STATE.COLLAPSED) return
            clearTimers()
            setOpen(true)
        },
        [clearTimers]
    )

    const onFocus = useCallback(() => {
        if (escapeDismissed.current || stateRef.current !== SIDEBAR_STATE.COLLAPSED) return
        if (!hasKeyboardFocusWithin(rootRef.current, true)) return
        clearTimers()
        setOpen(true)
    }, [clearTimers])

    const onBlur = useCallback(
        (event) => {
            if (rootRef.current && rootRef.current.contains(event.relatedTarget)) return
            escapeDismissed.current = false
            if (stateRef.current === SIDEBAR_STATE.PEEK && !hovering.current) scheduleClose()
        },
        [scheduleClose]
    )

    const onKeyDown = useCallback(
        (event) => {
            if (event.key !== 'Escape' || stateRef.current !== SIDEBAR_STATE.PEEK) return
            escapeDismissed.current = true
            collapse()
        },
        [collapse]
    )

    // The pin is only reachable while the panel is open, so both directions keep it
    // open: pinning just makes the page make room, unpinning leaves a peek that closes
    // once the pointer (or keyboard focus) leaves.
    const togglePin = useCallback(() => {
        const next = !pinnedPref
        userToggled.current = true
        clearTimers()
        setOpen(true)
        setPinnedPref(next)
        writeStoredCollapsed(!next)
    }, [pinnedPref, clearTimers])

    const consumeUserToggle = useCallback(() => {
        const toggled = userToggled.current
        userToggled.current = false
        return toggled
    }, [])

    return {
        rootRef,
        state,
        pinnedPref,
        showPin,
        togglePin,
        consumeUserToggle,
        handlers: { onPointerEnter, onPointerLeave, onPointerDown, onFocus, onBlur, onKeyDown },
    }
}

export default useSidebarState
